'use client';

import React, { useEffect, useMemo, useState } from 'react';

import { Treatment } from '@/types/components/whatIsYourConcern';

import RecommendedTreatmentsHeader from './RecommendedTreatmentsHeader';
import TreatmentAccordionItem from './TreatmentAccordionItem';
import TreatmentCard from './TreatmentCard';

type Props = {
  concernTitle?: string;
  treatments?: Treatment[];
};

const PREVIEW_COUNT = 3;

const RecommendedTreatmentsClient = ({
  concernTitle,
  treatments = [],
}: Props) => {
  const [showAll, setShowAll] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const [openMobileIndexes, setOpenMobileIndexes] = useState<number[]>([0]);

  useEffect(() => {
    setShowAll(false);
    setStartIndex(0);
    setOpenMobileIndexes([0]);
  }, [concernTitle]);

  const visibleTreatments = useMemo(() => {
    if (showAll) return treatments;
    return treatments.slice(startIndex, startIndex + PREVIEW_COUNT);
  }, [showAll, treatments, startIndex]);

  const canGoPrev = startIndex > 0;
  const canGoNext = startIndex + PREVIEW_COUNT < treatments.length;
  const canToggleViewAll = treatments.length > PREVIEW_COUNT;

  const goPrev = () => {
    if (!canGoPrev) return;
    setStartIndex((prev) => prev - 1);
  };

  const goNext = () => {
    if (!canGoNext) return;
    setStartIndex((prev) => prev + 1);
  };

  const toggleMobile = (index: number) => {
    setOpenMobileIndexes((prev) => {
      if (prev.includes(index)) {
        return prev.filter((item) => item !== index);
      }
      return [...prev, index];
    });
  };

  return (
    <div id="treatments" className="content-wrapper">
      <RecommendedTreatmentsHeader
        concernTitle={concernTitle}
        showAll={showAll}
        canToggleViewAll={canToggleViewAll}
        onToggleShowAll={() => setShowAll((prev) => !prev)}
      />

      <div className="my-4 flex flex-col gap-3 md:hidden lg:my-5">
        {treatments.map((treatment, index) => {
          const isOpen = openMobileIndexes.includes(index);

          return (
            <TreatmentAccordionItem
              key={index}
              treatment={treatment}
              isOpen={isOpen}
              onToggle={() => toggleMobile(index)}
            />
          );
        })}
      </div>

      <div className="hidden md:block">
        <div className="grid grid-cols-1 gap-8 px-1 py-2 md:grid-cols-2 xl:grid-cols-3">
          {visibleTreatments.map((treatment, index) => (
            <div key={index} className="flex min-w-0">
              <TreatmentCard treatment={treatment} compact={!showAll} />
            </div>
          ))}
        </div>

        {!showAll && canToggleViewAll ? (
          <div className="my-4 flex justify-center gap-3 lg:my-5">
            <button
              type="button"
              onClick={goPrev}
              disabled={!canGoPrev}
              className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-full transition ${
                canGoPrev
                  ? 'bg-surface-light text-theme-brand-blue'
                  : 'bg-border-subtle text-text-disabled-muted cursor-not-allowed'
              }`}
              aria-label="Previous"
            >
              ←
            </button>

            <button
              type="button"
              onClick={goNext}
              disabled={!canGoNext}
              className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-full transition ${
                canGoNext
                  ? 'bg-surface-light text-theme-brand-blue'
                  : 'bg-border-subtle text-text-disabled-muted cursor-not-allowed'
              }`}
              aria-label="Next"
            >
              →
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default RecommendedTreatmentsClient;
